import React from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';

import { black, white } from 'lib/css/colors';
import NutritionPanelDisclaimer from 'components/shared/nutrition_facts/NutritionPanelDisclaimer';
import NutritionPanelIngredients from 'components/shared/nutrition_facts/NutritionPanelIngredients';

/**
 * BetaNutritionFactsLabel — FDA-style "Nutrition Facts" panel for the beta
 * Product Details tab. Helvetica, black-bordered, mimicking the mockup's
 * `.nf-label` block exactly. Reads from the same `nutrition_facts` attribute
 * list the legacy PDP consumes — each nutrient has companion attributes:
 *
 *   <nutrient>_quantitycontained          → numeric value (e.g. "11")
 *   <nutrient>_uom                        → unit of measure (g, mg, mcg, …)
 *   <nutrient>_rdi_dailyvalueintakepercent → % Daily Value (e.g. "14")
 *
 * Rows with no quantity are skipped so the label stays tight on products with
 * partial nutrition data. The footnote is always rendered.
 */
function BetaNutritionFactsLabel({ attributes, ingredients }) {
  const get = name => {
    const found = (attributes || []).find(a => a && a.attribute_name === name);
    return found ? found.attribute_value : null;
  };

  const isNil = v => v === null || v === undefined || v === '';
  const fmtQty = (qty, uom) => {
    if (isNil(qty)) return '';
    const n = Number(qty);
    const q = Number.isNaN(n) ? qty : Math.round(n * 100) / 100;
    return uom ? `${q} ${uom}` : `${q}`;
  };
  const fmtDv = dv => (isNil(dv) ? '' : `${Math.round(Number(dv))}%`);

  // USDA attribute disclaimer. Not a nutrient row.
  const panelDisclaimer = get('nutrition_panel_disclaimer');

  // Servings header
  const servingsPer = get('number_of_servings_per_package');
  const ssQty = get('serving_size_quantity');
  const ssUom = get('serving_size_uom');
  const ssDesc = get('serving_size_description');
  const calories = get('calories_quantitycontained');

  // Main row spec — one entry per nutrient line.  `indent` controls left pad:
  //   0 — main (bold), 1 — sub (e.g. Saturated Fat), 2 — sub-sub (e.g. Added
  //   Sugars). `bold` controls whether the value column is bold.
  const ROWS = [
    {
      label: 'Total Fat',
      qty: 'total_fat_quantitycontained',
      uom: 'total_fat_uom',
      dv: 'total_fat_rdi_dailyvalueintakepercent',
      bold: true,
    },
    {
      label: 'Saturated Fat',
      qty: 'saturated_fat_quantitycontained',
      uom: 'saturated_fat_uom',
      dv: 'saturated_fat_rdi_dailyvalueintakepercent',
      indent: 1,
    },
    {
      label: 'Trans Fat',
      qty: 'transfatty_acids_quantitycontained',
      uom: 'transfatty_acids_uom',
      indent: 1,
      italicLabel: 'Trans',
    },
    {
      label: 'Cholesterol',
      qty: 'cholesterol_quantitycontained',
      uom: 'cholesterol_uom',
      dv: 'cholesterol_rdi_dailyvalueintakepercent',
      bold: true,
    },
    {
      label: 'Sodium',
      qty: 'sodium_quantitycontained',
      uom: 'sodium_uom',
      dv: 'sodium_rdi_dailyvalueintakepercent',
      bold: true,
    },
    {
      label: 'Total Carbohydrates',
      qty: 'carbohydrates_quantitycontained',
      uom: 'carbohydrates_uom',
      dv: 'carbohydrates_rdi_dailyvalueintakepercent',
      bold: true,
    },
    {
      label: 'Dietary Fiber',
      qty: 'total_dietary_fiber_quantitycontained',
      uom: 'total_dietary_fiber_uom',
      dv: 'total_dietary_fiber_rdi_dailyvalueintakepercent',
      indent: 1,
    },
    {
      label: 'Total Sugars',
      qty: 'total_sugars_quantitycontained',
      uom: 'total_sugars_uom',
      indent: 1,
    },
    {
      label: 'Includes Added Sugars',
      qty: 'added_sugars_quantitycontained',
      uom: 'added_sugars_uom',
      dv: 'added_sugars_rdi_dailyvalueintakepercent',
      indent: 2,
      preserveLabel: true,
    },
    {
      label: 'Protein',
      qty: 'protein_quantitycontained',
      uom: 'protein_uom',
      bold: true,
    },
  ];

  // Optional micronutrient rows below the medium separator. Same data shape
  // as the main rows — but rendered without bold and no indent.
  const MICRO_ROWS = [
    {
      label: 'Vitamin D',
      qty: 'vitamin_d_quantitycontained',
      uom: 'vitamin_d_uom',
      dv: 'vitamin_d_rdi_dailyvalueintakepercent',
    },
    {
      label: 'Calcium',
      qty: 'calcium_quantitycontained',
      uom: 'calcium_uom',
      dv: 'calcium_rdi_dailyvalueintakepercent',
    },
    {
      label: 'Iron',
      qty: 'iron_quantitycontained',
      uom: 'iron_uom',
      dv: 'iron_rdi_dailyvalueintakepercent',
    },
    {
      label: 'Potassium',
      qty: 'potassium_quantitycontained',
      uom: 'potassium_uom',
      dv: 'potassium_rdi_dailyvalueintakepercent',
    },
    {
      label: 'Vitamin A',
      qty: 'vitamin_a_quantitycontained',
      uom: 'vitamin_a_uom',
      dv: 'vitamin_a_rdi_dailyvalueintakepercent',
    },
    {
      label: 'Vitamin C',
      qty: 'vitamin_c_quantitycontained',
      uom: 'vitamin_c_uom',
      dv: 'vitamin_c_rdi_dailyvalueintakepercent',
    },
  ];

  // Resolve each row with the live attribute values. Skip rows whose
  // quantity is missing — sparse products shouldn't show empty lines.
  const resolveRow = row => {
    const qty = get(row.qty);
    if (isNil(qty)) return null;
    const uom = get(row.uom);
    const dv = row.dv ? get(row.dv) : null;
    return {
      ...row,
      qtyText: fmtQty(qty, uom),
      dvText: fmtDv(dv),
    };
  };

  const mainRows = ROWS.map(resolveRow).filter(Boolean);
  const microRows = MICRO_ROWS.map(resolveRow).filter(Boolean);
  const hasContent =
    !isNil(servingsPer) ||
    !isNil(ssQty) ||
    !isNil(calories) ||
    mainRows.length > 0 ||
    microRows.length > 0;

  if (!hasContent) return null;

  const renderRow = r => (
    <React.Fragment key={r.label}>
      <hr className={css(styles.hr)} />
      <div
        className={css(
          styles.row,
          r.bold && styles.rowBold,
          r.indent === 1 && styles.rowIndent,
          r.indent === 2 && styles.rowIndent2,
        )}
      >
        <span>
          {r.italicLabel ? (
            <>
              <i>{r.italicLabel}</i>
              {` ${r.label.replace(r.italicLabel, '').trim()} ${r.qtyText}`}
            </>
          ) : (
            `${r.label} ${r.qtyText}`
          )}
        </span>
        {r.dvText && <span>{r.dvText}</span>}
      </div>
    </React.Fragment>
  );

  return (
    <>
      <NutritionPanelDisclaimer disclaimer={panelDisclaimer} className={css(styles.disclaimer)} />
      <div className={css(styles.label)}>
        <div className={css(styles.title)}>Nutrition Facts</div>
        <hr className={css(styles.hr)} />
        {!isNil(servingsPer) && (
          <div className={css(styles.sub)}>{servingsPer} servings per container</div>
        )}
        {(!isNil(ssQty) || !isNil(ssDesc)) && (
          <div className={css(styles.row, styles.rowBold)}>
            <span>Serving size</span>
            <span>{ssDesc || fmtQty(ssQty, ssUom)}</span>
          </div>
        )}
        <hr className={css(styles.hrThick)} />
        <div className={css(styles.subBold)}>Amount per serving</div>
        {!isNil(calories) && (
          <div className={css(styles.calRow)}>
            <span className={css(styles.calLbl)}>Calories</span>
            <span className={css(styles.calVal)}>{Math.round(Number(calories))}</span>
          </div>
        )}
        <hr className={css(styles.hrMed)} />
        <div className={css(styles.dvHdr)}>% Daily Value*</div>
        {mainRows.map(renderRow)}
        {microRows.length > 0 && <hr className={css(styles.hrMed)} />}
        {microRows.map(renderRow)}
        <hr className={css(styles.hrMed)} />
        <div className={css(styles.footnote)}>
          * The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to
          a daily diet. 2,000 calories a day is used for general nutrition advice.
        </div>
        {ingredients && <hr className={css(styles.hr)} />}
        <NutritionPanelIngredients
          ingredients={ingredients}
          className={css(styles.ingredients)}
          labelClassName={css(styles.ingredientsLabel)}
          valueClassName={css(styles.ingredientsValue)}
        />
      </div>
    </>
  );
}

BetaNutritionFactsLabel.propTypes = {
  attributes: PropTypes.arrayOf(
    PropTypes.shape({
      attribute_name: PropTypes.string,
      attribute_value: PropTypes.any,
    }),
  ),
  ingredients: PropTypes.string,
};

BetaNutritionFactsLabel.defaultProps = {
  attributes: [],
  ingredients: null,
};

const styles = StyleSheet.create({
  disclaimer: {
    border: `1px solid ${black}`,
    backgroundColor: white,
    borderRadius: 6,
    padding: '8px 10px',
    fontFamily: 'Helvetica, Arial, sans-serif',
    fontSize: 11,
    lineHeight: 1.3,
    color: black,
    maxWidth: 380,
    marginTop: 8,
  },
  label: {
    border: '2px solid #000',
    backgroundColor: '#fff',
    borderRadius: 6,
    padding: '12px 14px',
    fontFamily: 'Helvetica, Arial, sans-serif',
    color: '#000',
    maxWidth: 380,
    lineHeight: 1.15,
    marginTop: 8,
  },
  title: {
    fontFamily: 'Helvetica, Arial, sans-serif',
    fontWeight: 900,
    fontSize: 30,
    lineHeight: 1,
    letterSpacing: '-0.02em',
  },
  sub: {
    fontSize: 11,
    color: '#000',
  },
  subBold: {
    fontSize: 11,
    color: '#000',
    fontWeight: 700,
    marginBottom: 2,
  },
  hr: {
    border: 0,
    borderTop: '1px solid #000',
    margin: '8px 0',
  },
  hrMed: {
    border: 0,
    borderTop: '4px solid #000',
    margin: '8px 0',
  },
  hrThick: {
    border: 0,
    borderTop: '8px solid #000',
    margin: '8px 0',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    margin: '3px 0',
    fontFamily: 'Helvetica, Arial, sans-serif',
    fontSize: 12,
    fontWeight: 400,
  },
  rowBold: {
    fontWeight: 700,
  },
  rowIndent: {
    paddingLeft: 14,
  },
  rowIndent2: {
    paddingLeft: 28,
  },
  calRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    margin: 0,
  },
  calLbl: {
    fontWeight: 700,
    fontSize: 18,
  },
  calVal: {
    fontWeight: 900,
    fontSize: 36,
    lineHeight: 1,
  },
  dvHdr: {
    textAlign: 'right',
    fontWeight: 700,
    fontSize: 10,
    marginBottom: 4,
  },
  footnote: {
    fontSize: 9.5,
    lineHeight: 1.3,
    marginTop: 6,
  },
  ingredients: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    fontSize: 11,
    lineHeight: 1.3,
    marginTop: 4,
  },
  ingredientsLabel: {
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  ingredientsValue: {
    textAlign: 'right',
    fontWeight: 700,
    wordBreak: 'break-word',
  },
});

export default BetaNutritionFactsLabel;
